import { forwardRef, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConditionalWrapper } from 'components/Shared/ConditionalWrapper';
import { Portal } from 'components/Shared/Portal';
import { useEventbus } from 'hooks/useEventbus';
import * as Styled from './styles';
import { StepViewerEvents } from 'constants/app/eventbus-events';

type StepViewerProgressNavigationProps = {
  nextBtnText?: string;
  prevBtnText?: string;
  info: {
    show?: boolean;
    text?: string | ReactElement;
    currentStep: number;
    totalSteps?: number;
  };
  withoutWrapper?: boolean;
  handleToNextStep?: boolean;
  eventId?: string;
  fixedFooterId?: string;
  blockButtons?: boolean;
};

export const StepViewerProgressNavigation = forwardRef<
  HTMLDivElement | null,
  StepViewerProgressNavigationProps
>(
  (
    {
      nextBtnText,
      prevBtnText,
      info,
      withoutWrapper,
      handleToNextStep = true,
      eventId,
      fixedFooterId,
      blockButtons = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [nextBtnDisabled, setNextBtnDisabled] = useState(false);
    const [controlsDisabled, setControlsDisabled] = useState(false);
    const [controlsHidden, setControlsHidden] = useState(false);
    const [nextBtnLoading, setNextBtnLoading] = useState(false);
    const { fireEvent } = useEventbus(
      {
        onEvent: ({ key, id, payload }) => {
          if (id !== eventId) return;
          switch (key) {
            case StepViewerEvents.DisableProceeding:
              setNextBtnDisabled(true);
              break;
            case StepViewerEvents.EnableProceeding:
              setNextBtnDisabled(false);
              break;
            case StepViewerEvents.DisableControls:
              setControlsDisabled(true);
              break;
            case StepViewerEvents.EnableControls:
              setControlsDisabled(false);
              break;
            case StepViewerEvents.SetNextLoading:
              setNextBtnLoading(true);
              break;
            case StepViewerEvents.UnsetNextLoading:
              setNextBtnLoading(false);
              break;
            case StepViewerEvents.HideControls:
              setControlsHidden(true);
              break;
            case StepViewerEvents.ShowControls:
              setControlsHidden(false);
              break;
            case StepViewerEvents.NextAction:
              nextBtnOnClick();
              break;
            case StepViewerEvents.PreviousAction:
              prevBtnOnClick();
              break;
            case StepViewerEvents.GoToStep:
              goToStep(payload);
              break;
          }
        }
      },
      []
    );
    const {
      show: infoShow = true,
      text: infoText,
      currentStep,
      totalSteps
    } = info;

    const renderInfoBlock = () => {
      let info = null;
      if (!infoShow) return null;
      if (typeof infoText === 'undefined') {
        info = t('generic.stepsProgressCurrentOfTotal', {
          current: currentStep,
          total: totalSteps
        });
      } else {
        info = infoText;
      }
      return (
        <Styled.StepViewerProgressNavigationInfo>
          {info}
        </Styled.StepViewerProgressNavigationInfo>
      );
    };

    const prevBtnOnClick = () => {
      fireEvent({
        key: StepViewerEvents.RenderPreviousView,
        id: eventId
      });
    };

    const nextBtnOnClick = () => {
      fireEvent({
        key: handleToNextStep
          ? StepViewerEvents.RenderNextView
          : StepViewerEvents.InitiateNextViewRender,
        id: eventId
      });
      if (currentStep === totalSteps) {
        fireEvent({
          key: StepViewerEvents.FinishSteps,
          id: eventId
        });
      }
    };

    const goToStep = (step: number) => {
      if (!totalSteps) return;
      if (step > totalSteps) return;
      if (step < 1) return;
      fireEvent({
        key: StepViewerEvents.RenderStep,
        id: eventId,
        payload: step
      });
    };

    return (
      <Styled.StepViewerProgressNavigation
        $withoutWrapper={withoutWrapper || !!fixedFooterId}
        ref={ref}
      >
        {renderInfoBlock()}
        {!controlsHidden && (
          <ConditionalWrapper
            condition={!!fixedFooterId}
            wrapper={children => (
              <Portal rootId={fixedFooterId}>{children}</Portal>
            )}
          >
            <Styled.StepViewerProgressNavigationButtonsGroup
              $block={blockButtons}
            >
              {currentStep > 1 && (
                <Styled.StepViewerProgressNavigationButton
                  type="secondary"
                  onClick={prevBtnOnClick}
                  block={blockButtons}
                  disabled={controlsDisabled}
                >
                  {prevBtnText || t('generic.back')}
                </Styled.StepViewerProgressNavigationButton>
              )}
              <Styled.StepViewerProgressNavigationButton
                type="primary"
                onClick={nextBtnOnClick}
                loading={nextBtnLoading}
                block={blockButtons}
                disabled={controlsDisabled || nextBtnDisabled}
              >
                {nextBtnText || t('generic.next')}
              </Styled.StepViewerProgressNavigationButton>
            </Styled.StepViewerProgressNavigationButtonsGroup>
          </ConditionalWrapper>
        )}
      </Styled.StepViewerProgressNavigation>
    );
  }
);
